/**
 * useMarkVariantPosted — `POST /v1/off-page-briefs/:opbId/variants/:variantId/mark-posted`.
 *
 * Primary manual-posting path. Body: `{ posted_url }`. The API validates
 * that the URL host matches the platform's canonical host and returns
 * `POSTED_URL_HOST_MISMATCH` (VALIDATION) when it doesn't — the caller
 * pattern-matches on `error.code` to surface an inline field error under
 * the URL input.
 *
 * On success the detail cache is invalidated so the variant row's status +
 * `posted_url` update without a manual refetch.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  MarkVariantPostedRequest,
  OffPageBriefVariant,
} from '@seo/types';
import { apiPost } from '@/lib/api';

export interface UseMarkVariantPostedArgs {
  variantId: string;
  body: MarkVariantPostedRequest;
}

export function useMarkVariantPosted(opbId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ variantId, body }: UseMarkVariantPostedArgs) =>
      apiPost<OffPageBriefVariant>(
        `/v1/off-page-briefs/${opbId}/variants/${variantId}/mark-posted`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'detail', opbId],
      });
    },
  });
}
